import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useOutletContext, useSearchParams} from "react-router-dom";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import Button from "../../../../../components/Button";
import {useBusiness} from "../../../../../context/BusinessContext";
import {useAuth} from "../../../../../context/AuthContext";
import {useParams} from "react-router";
import {MarkdownField} from "../../../../../components/MarkdownField";
import SectionHeading from "../../../../../components/SectionHeading";
import {CalendarDays, Globe, Layers, Lock, MapPin, Monitor, RefreshCw, SaveIcon, Trophy, UserCheck} from "lucide-react";

type OutletCtx = { eventId: string | null; isEdit?: boolean };

type EventType = "PUBLIC" | "PRIVATE" | "INVITE_ONLY";
type EventLocationType = "PHYSICAL" | "VIRTUAL" | "HYBRID";
type EventKind = "STANDARD" | "TOURNAMENT";
type VirtualPlatform = "ZOOM" | "TEAMS" | "GOOGLE_MEET" | "WEBEX" | "YOUTUBE_LIVE" | "OTHER";
type RecurrencePattern = "DAILY" | "WEEKLY" | "BIWEEKLY" | "MONTHLY" | "YEARLY";
type Currency = "NAD" | "ZAR" | "USD" | "EUR" | "NGN" | "BWP" | "GHS" | "ZMW" | "KES";
/** The online gateway an event's card checkout runs on. Only a platform administrator may change it. */
type PaymentProvider = "FINATIC" | "STRIPE" | "DPO";

type DetailsPayload = {
    eventKind: EventKind;
    name: string;
    category: string;
    subCategory: string;
    tags: string[];
    description?: string;
    startDate: string;
    endDate: string;
    location: string;
    address: string;
    eventType: EventType;
    locationType: EventLocationType;
    virtualPlatform: VirtualPlatform | "";
    virtualLink: string;
    recurring: boolean;
    recurrencePattern: RecurrencePattern | "";
    recurrenceInterval: number | "";
    recurrenceEndDate: string;
    currency: Currency;
    paymentProvider: PaymentProvider;
};

function isoToLocalInput(iso?: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";

    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes()
    )}`;
}

function localInputToBackendLocal(local: string): string {
    return (local || "").trim();
}

function useDebouncedValue<T>(value: T, delayMs: number) {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(t);
    }, [value, delayMs]);

    return debounced;
}

type ConfidenceLevel = "low" | "medium" | "high";

function computeConfidence(addr: string): { level: ConfidenceLevel; reason: string } {
    const s = (addr || "").trim();

    if (!s) return {level: "low", reason: "No address yet"};
    const hasNumber = /\d/.test(s);
    const hasComma = s.includes(",");
    const longEnough = s.length >= 18;

    if (hasNumber && hasComma && longEnough) return {level: "high", reason: "Looks complete"};
    if ((hasComma && longEnough) || (hasNumber && longEnough)) return {level: "medium", reason: "Might be enough"};
    return {level: "low", reason: "Add street + city for better results"};
}

function confidenceBadge(level: ConfidenceLevel) {
    switch (level) {
        case "high":
            return "bg-green-50 text-green-700 ring-green-200";
        case "medium":
            return "bg-yellow-50 text-yellow-800 ring-yellow-200";
        default:
            return "bg-gray-50 text-gray-700 ring-gray-200";
    }
}

const EVENT_KIND_OPTIONS: { value: EventKind; label: string; description: string; icon: React.ReactNode }[] = [
    {
        value: "STANDARD",
        label: "Standard Event",
        description: "A regular event with tickets — concerts, conferences, markets, and more.",
        icon: <CalendarDays className="size-5"/>,
    },
    {
        value: "TOURNAMENT",
        label: "Tournament",
        description: "A competitive event with play categories, registrations, and brackets. Set up the competition from the Tournaments area after creating it.",
        icon: <Trophy className="size-5"/>,
    },
];

const EVENT_TYPE_OPTIONS: { value: EventType; label: string; description: string; icon: React.ReactNode }[] = [
    {
        value: "PUBLIC",
        label: "Public",
        description: "Listed publicly and discoverable by anyone browsing events. Great for open community events.",
        icon: <Globe className="size-5"/>,
    },
    {
        value: "PRIVATE",
        label: "Private",
        description: "Not listed publicly. Only people with a direct link can find and register for your event.",
        icon: <Lock className="size-5"/>,
    },
    {
        value: "INVITE_ONLY",
        label: "Invite Only",
        description: "Attendance is by invitation only. You control exactly who receives access to register.",
        icon: <UserCheck className="size-5"/>,
    },
];

const LOCATION_TYPE_OPTIONS: {
    value: EventLocationType;
    label: string;
    description: string;
    icon: React.ReactNode
}[] = [
    {
        value: "PHYSICAL",
        label: "In Person",
        description: "Held at a specific venue. Attendees come to you in person at a physical location.",
        icon: <MapPin className="size-5"/>,
    },
    {
        value: "VIRTUAL",
        label: "Online",
        description: "Hosted entirely online via a streaming or video platform. No venue required.",
        icon: <Monitor className="size-5"/>,
    },
    {
        value: "HYBRID",
        label: "Hybrid",
        description: "Combines an in-person venue with an online stream so both local and remote attendees can join.",
        icon: <Layers className="size-5"/>,
    },
];

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
    {value: "Art & Culture", label: "Art & Culture"},
    {value: "Business", label: "Business"},
    {value: "Comedy", label: "Comedy"},
    {value: "Community", label: "Community"},
    {value: "Conferences", label: "Conferences"},
    {value: "Dance", label: "Dance"},
    {value: "Education", label: "Education"},
    {value: "Entertainment", label: "Entertainment"},
    {value: "Environment", label: "Environment"},
    {value: "Family & Kids", label: "Family & Kids"},
    {value: "Fashion", label: "Fashion"},
    {value: "Festival", label: "Festival"},
    {value: "Fitness", label: "Fitness"},
    {value: "Food", label: "Food"},
    {value: "Fundraiser", label: "Fundraiser"},
    {value: "Gaming & eSports", label: "Gaming & eSports"},
    {value: "Health & Wellness", label: "Health & Wellness"},
    {value: "Holiday & Seasonal", label: "Holiday & Seasonal"},
    {value: "Lifestyle", label: "Lifestyle"},
    {value: "Markets", label: "Markets"},
    {value: "Music", label: "Music"},
    {value: "Networking", label: "Networking"},
    {value: "Nightlife", label: "Nightlife"},
    {value: "Photography & Film", label: "Photography & Film"},
    {value: "Religious & Spiritual", label: "Religious & Spiritual"},
    {value: "Sports", label: "Sports"},
    {value: "Tech & Innovation", label: "Tech & Innovation"},
    {value: "Theatre & Drama", label: "Theatre & Drama"},
    {value: "Travel & Outdoor", label: "Travel & Outdoor"},
    {value: "Workshops", label: "Workshops"},
];

const SUBCATEGORY_MAP: Record<string, string[]> = {
    "Sports": ["Angling", "Athletics", "Badminton", "Baseball", "Basketball", "Beach Volleyball", "Boxing", "Chess", "Climbing", "Cricket", "Cycling", "Darts", "Equestrian", "Golf", "Gymnastics", "Handball", "Hiking", "Hockey", "Motorsport", "Netball", "Padel", "Rugby", "Running", "Skateboarding", "Soccer", "Squash", "Surfing", "Swimming", "Table Tennis", "Tennis", "Triathlon", "Volleyball"],
    "Music": ["Acoustic", "Afrobeats", "Amapiano", "Blues", "Choir", "Classical", "Country", "DJ Set", "Electronic", "Gospel", "Hip Hop", "Jazz", "Kwaito", "Live Concert", "Metal", "Open Mic Night", "Pop", "R&B & Soul", "Reggae", "Rock"],
    "Fitness": ["Aerobics", "Bootcamp", "CrossFit", "Cycling Class", "Dance Fitness", "HIIT", "Martial Arts", "Pilates", "Running Club", "Spin Class", "Strength Training", "Yoga", "Zumba"],
    "Food": ["Baking Class", "Beer Tasting", "Braai & BBQ", "Coffee Tasting", "Cooking Class", "Food Market", "Food Tasting", "Pop-Up Dinner", "Street Food", "Wine Tasting"],
    "Tech & Innovation": ["AI & Machine Learning", "Blockchain & Web3", "Coding Bootcamp", "Cybersecurity", "Demo Day", "Hackathon", "Product Launch", "Startup Pitch", "Tech Meetup", "Webinar"],
    "Conferences": ["Convention", "Expo", "Forum", "Keynote", "Panel Discussion", "Summit", "Symposium", "Trade Show"],
    "Workshops": ["Certification", "Creative Workshop", "DIY Workshop", "Masterclass", "Skills Training", "Writing Workshop"],
    "Art & Culture": ["Art Class", "Craft Fair", "Cultural Showcase", "Exhibition", "Gallery", "Heritage Event", "Museum Tour", "Poetry Reading", "Pottery & Ceramics"],
    "Dance": ["African Dance", "Ballet", "Ballroom", "Contemporary", "Dance Battle", "Hip Hop Dance", "Latin", "Salsa", "Social Dance", "Tap"],
    "Entertainment": ["Circus", "Film Screening", "Game Show", "Magic Show", "Puppet Show", "Talent Show", "Trivia Night", "Variety Show"],
    "Nightlife": ["Bar Crawl", "Club Night", "Karaoke", "Ladies Night", "Live DJ", "Rooftop Party", "Silent Disco", "Themed Party"],
    "Business": ["AGM", "Award Ceremony", "Conference", "Corporate Function", "Gala Dinner", "Networking Mixer", "Product Launch", "Team Building"],
    "Festival": ["Arts Festival", "Beer Festival", "Cultural Festival", "Food Festival", "Music Festival", "Street Festival", "Wine Festival"],
    "Theatre & Drama": ["Improv", "Musical", "One-Act Play", "Opera", "Pantomime", "Play", "Spoken Word"],
    "Health & Wellness": ["Fitness Retreat", "Health Talk", "Meditation", "Mental Health", "Nutrition Workshop", "Sound Healing", "Spa Day", "Wellness Retreat"],
    "Networking": ["Alumni Event", "Business Breakfast", "Career Fair", "Industry Meetup", "Mentorship Session", "Speed Networking"],
    "Comedy": ["Comedy Festival", "Comedy Roast", "Improv Comedy", "Open Mic", "Sketch Show", "Stand-Up Comedy"],
    "Community": ["Community Clean-Up", "Fundraiser Drive", "Neighbourhood Meetup", "Support Group", "Town Hall", "Volunteer Event"],
    "Education": ["Exam Prep", "Guest Speaker", "Language Class", "Lecture", "Seminar", "Study Group", "Tutoring"],
    "Travel & Outdoor": ["Adventure Tour", "Bird Watching", "Camping", "Guided Tour", "Kayaking", "Mountain Biking", "Nature Walk", "Road Trip", "Safari"],
    "Gaming & eSports": ["Board Game Night", "Card Game Night", "eSports League", "Gaming Expo", "LAN Party", "Retro Gaming", "Tournament", "VR Experience"],
    "Environment": ["Beach Clean-Up", "Conservation", "Eco Fair", "Recycling Drive", "Sustainability Talk", "Tree Planting"],
    "Family & Kids": ["Face Painting", "Family Fun Day", "Kids Party", "Kids Workshop", "Petting Zoo", "School Event", "Storytelling"],
    "Holiday & Seasonal": ["Christmas Event", "Easter Event", "Halloween Event", "Heritage Day", "Independence Day", "New Year Event", "Seasonal Market", "Valentine's Event"],
    "Photography & Film": ["Cinematography Workshop", "Documentary Screening", "Film Festival", "Photo Exhibition", "Photo Walk", "Photography Workshop", "Short Film Screening"],
    "Lifestyle": ["Beauty Event", "Fashion Show", "Home & Decor", "Lifestyle Expo", "Wedding Fair", "Wellness Fair"],
    "Fashion": ["Designer Showcase", "Fashion Market", "Fashion Week", "Pop-Up Shop", "Runway Show", "Trunk Show"],
    "Markets": ["Art Market", "Book Market", "Craft Market", "Farmers Market", "Flea Market", "Night Market", "Vintage Market"],
    "Religious & Spiritual": ["Bible Study", "Church Service", "Faith Conference", "Leadership Equip", "Marriage Enrichment", "Prayer Meeting", "Spiritual Retreat", "Worship Night", "Youth Service"],
    "Fundraiser": ["Auction", "Bake Sale", "Charity Concert", "Charity Gala", "Donation Drive", "Fun Run", "Golf Day", "Raffle"],
};

const VIRTUAL_PLATFORM_OPTIONS: { value: VirtualPlatform; label: string }[] = [
    {value: "ZOOM", label: "Zoom"},
    {value: "TEAMS", label: "Microsoft Teams"},
    {value: "GOOGLE_MEET", label: "Google Meet"},
    {value: "WEBEX", label: "Webex"},
    {value: "YOUTUBE_LIVE", label: "YouTube Live"},
    {value: "OTHER", label: "Other"},
];

const PAYMENT_PROVIDER_OPTIONS: { value: PaymentProvider; label: string }[] = [
    {value: "FINATIC", label: "Finatic (charges in NAD)"},
    {value: "STRIPE", label: "Stripe (charges in the event currency)"},
    {value: "DPO", label: "DPO Pay (charges in NAD)"},
];

const CURRENCY_OPTIONS: { value: Currency; label: string }[] = [
    {value: "NAD", label: "NAD — Namibian Dollar"},
    {value: "ZAR", label: "ZAR — South African Rand"},
    {value: "USD", label: "USD — US Dollar"},
    {value: "EUR", label: "EUR — Euro"},
    {value: "NGN", label: "NGN — Nigerian Naira"},
    {value: "BWP", label: "BWP — Botswana Pula"},
    {value: "GHS", label: "GHS — Ghanaian Cedi"},
    {value: "ZMW", label: "ZMW — Zambian Kwacha"},
    {value: "KES", label: "KES — Kenyan Shilling"},
];

function CardSelector<T extends string>({
                                            options,
                                            value,
                                            onChange,
                                        }: {
    options: { value: T; label: string; description: string; icon: React.ReactNode }[];
    value: T;
    onChange: (v: T) => void;
}) {
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {options.map((opt) => {
                const selected = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        className={`flex w-full items-start gap-4 rounded-lg border p-4 text-left transition-all focus:outline-none ${
                            selected
                                ? "border-orange-600 bg-orange-50 ring-1 ring-orange-500"
                                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                        <span
                            className={`mt-0.5 shrink-0 rounded-lg p-2 ${selected ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"}`}>
                            {opt.icon}
                        </span>
                        <span className="flex flex-col">
                            <span className={`text-sm font-semibold ${selected ? "text-orange-700" : "text-gray-900"}`}>
                                {opt.label}
                            </span>
                            <span
                                className={`mt-1 text-xs leading-relaxed ${selected ? "text-orange-700" : "text-gray-500"}`}>
                                {opt.description}
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export function DetailsStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const {user} = useAuth();
    const isAdmin = user?.roles?.includes("ADMINISTRATOR") ?? false;
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp, setSp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState<DetailsPayload>({
        eventKind: sp.get("kind") === "TOURNAMENT" ? "TOURNAMENT" : "STANDARD",
        name: "",
        category: "Food",
        subCategory: "",
        tags: [],
        description: "",
        startDate: "",
        endDate: "",
        location: "",
        address: "",
        eventType: "PUBLIC",
        locationType: "PHYSICAL",
        virtualPlatform: "",
        virtualLink: "",
        recurring: false,
        recurrencePattern: "",
        recurrenceInterval: "",
        recurrenceEndDate: "",
        currency: "NAD",
        paymentProvider: "FINATIC",
    });

    const [tagInput, setTagInput] = useState("");

    const [appendCountry, setAppendCountry] = useState(true);

    const debouncedAddress = useDebouncedValue(form.address, 600);
    const debouncedVenue = useDebouncedValue(form.location, 600);

    const fullQuery = useMemo(() => {
        const parts = [debouncedVenue?.trim(), debouncedAddress?.trim()].filter(Boolean);
        const base = parts.join(", ");
        if (!base) return "";
        return appendCountry ? `${base}, Namibia` : base;
    }, [debouncedVenue, debouncedAddress, appendCountry]);

    const {level, reason} = useMemo(() => computeConfidence(form.address), [form.address]);
    const mapQuery = fullQuery ? encodeURIComponent(fullQuery) : null;

    const dateError = useMemo(() => {
        if (!form.startDate || !form.endDate) return null;
        const start = new Date(form.startDate).getTime();
        const end = new Date(form.endDate).getTime();
        if (end <= start) return "End date must be after the start date.";
        return null;
    }, [form.startDate, form.endDate]);

    const handleChange = (key: keyof DetailsPayload, value: string) => {
        setForm((prev) => ({...prev, [key]: value}));
    };

    const handleCategoryChange = (value: string) => {
        setForm((prev) => ({...prev, category: value, subCategory: ""}));
    };

    const addTag = (raw: string) => {
        const tag = raw.trim();
        if (!tag || form.tags.includes(tag)) return;
        setForm((prev) => ({...prev, tags: [...prev.tags, tag]}));
        setTagInput("");
    };

    const removeTag = (tag: string) => {
        setForm((prev) => ({...prev, tags: prev.tags.filter((t) => t !== tag)}));
    };

    const isPhysical = form.locationType === "PHYSICAL" || form.locationType === "HYBRID";
    const isVirtual = form.locationType === "VIRTUAL" || form.locationType === "HYBRID";

    useEffect(() => {
        if (!businessId || !eventId) return;

        setLoading(true);
        (async () => {
            try {
                const {data} = await api.get(`/api/v1/business/${businessId}/events/${eventId}`);

                setForm({
                    eventKind: data?.eventKind === "TOURNAMENT" ? "TOURNAMENT" : "STANDARD",
                    name: data?.name ?? data?.title ?? "",
                    category: data?.category ?? "Food",
                    subCategory: data?.subCategory ?? "",
                    tags: data?.tags ?? [],
                    description: data?.description ?? "",
                    startDate: isoToLocalInput(data?.startDate ?? data?.startTime),
                    endDate: isoToLocalInput(data?.endDate ?? data?.endTime),
                    location: data?.location ?? "",
                    address: data?.address ?? "",
                    eventType: data?.eventType ?? "PUBLIC",
                    locationType: data?.locationType ?? "PHYSICAL",
                    virtualPlatform: data?.virtualPlatform ?? "",
                    virtualLink: data?.virtualLink ?? "",
                    recurring: data?.recurring ?? false,
                    recurrencePattern: data?.recurrencePattern ?? "",
                    recurrenceInterval: data?.recurrenceInterval ?? "",
                    recurrenceEndDate: isoToLocalInput(data?.recurrenceEndDate),
                    currency: data?.currency ?? "NAD",
                    paymentProvider: data?.paymentProvider ?? "FINATIC",
                });
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load event details"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, eventId]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!businessId) return;
        if (dateError) {
            notify.error(dateError);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...form,
                startDate: form.startDate ? localInputToBackendLocal(form.startDate) : null,
                endDate: form.endDate ? localInputToBackendLocal(form.endDate) : null,
                virtualPlatform: form.virtualPlatform || null,
                virtualLink: form.virtualLink || null,
                subCategory: form.subCategory || null,
                recurrencePattern: form.recurring ? (form.recurrencePattern || null) : null,
                recurrenceInterval: form.recurring ? (form.recurrenceInterval !== "" ? Number(form.recurrenceInterval) : null) : null,
                recurrenceEndDate: form.recurring && form.recurrenceEndDate ? localInputToBackendLocal(form.recurrenceEndDate) : null,
                // Only an administrator may choose the gateway, so nobody else sends it at all.
                paymentProvider: isAdmin ? form.paymentProvider : undefined,
            };

            let id = eventId;

            if (!id) {
                const {data} = await api.post(`/api/v1/business/${businessId}/events`, payload);
                id = String(data?.id ?? data);

                if (!id) {
                    notify.error("Event created but no ID returned. Please contact support.");
                    return;
                }

                sp.set("eventId", id);
                setSp(sp, {replace: true});
            } else {
                await api.patch(`/api/v1/business/${businessId}/events/${id}`, payload);
            }

            notify.success("Saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save event details"));
        } finally {
            setSaving(false);
        }
    };

    const goNext = () => {
        if (!eventId) return;
        // Tournament metadata lives on the event; the competition itself (images,
        // play categories, registrations, brackets) is configured from the
        // Tournaments area, so send the user there instead of the ticket flow.
        if (form.eventKind === "TOURNAMENT") {
            navigate("/business/tournaments");
            return;
        }
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/event-days`);
        else navigate(`/business/events/new/event-days?eventId=${encodeURIComponent(eventId)}`);
    };

    const canContinue = !!eventId;
    const isTournament = form.eventKind === "TOURNAMENT";

    return (
        <div className="space-y-6">

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading event…</div>
            ) : (
                <form onSubmit={onSubmit} className="space-y-6">

                    <SectionHeading
                        title={mode === "edit" ? "Edit Event Details" : "Create Your Event"}
                        description={mode === "edit" ? "Update your event details." : "To create an event, please start by completing the form below."}
                        actions={
                            <>
                                <Button type="submit" variant="primary" size="md" disabled={saving}>
                                    {saving ? "Saving…" :
                                        <span className="inline-flex items-center gap-2"><SaveIcon
                                            className="size-4"/>Save</span>}
                                </Button>
                            </>
                        }
                    />

                    {/* Event kind — only chosen at creation; a tournament also creates a linked competition */}
                    {mode === "create" && (
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                            <div className="mb-3">
                                <p className="text-sm font-medium text-gray-900">What are you creating? *</p>
                                <p className="mt-0.5 text-xs text-gray-500">Choose a standard event or a tournament. This
                                    can't be changed later.</p>
                            </div>
                            <CardSelector
                                options={EVENT_KIND_OPTIONS}
                                value={form.eventKind}
                                onChange={(v) => handleChange("eventKind", v)}
                            />
                        </div>
                    )}

                    {/* Event Type */}
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="mb-3">
                            <p className="text-sm font-medium text-gray-900">Event type *</p>
                            <p className="mt-0.5 text-xs text-gray-500">Choose who can see and access your event.</p>
                        </div>
                        <CardSelector
                            options={EVENT_TYPE_OPTIONS}
                            value={form.eventType}
                            onChange={(v) => handleChange("eventType", v)}
                        />
                    </div>

                    {/* Core details */}
                    <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            {/* Event Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900">Event name *</label>
                                <p className="mt-1 text-xs text-gray-500">Be clear with a title that tells people what
                                    your event is about.</p>
                                <input
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                />
                            </div>

                            {/* Default currency */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900">Default currency *</label>
                                <p className="mt-1 text-xs text-gray-500">All ticket prices and fees for this event will
                                    be in this currency.</p>
                                <select
                                    required
                                    value={form.currency}
                                    onChange={(e) => handleChange("currency", e.target.value)}
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                >
                                    {CURRENCY_OPTIONS.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Payment provider: a platform administrator decision, so hidden from
                                everyone else. It cannot change once the event has orders. */}
                            {isAdmin && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Payment provider</label>
                                    <p className="mt-1 text-xs text-gray-500">The gateway that takes card payments
                                        for this event. Locked once the event has orders.</p>
                                    <select
                                        value={form.paymentProvider}
                                        onChange={(e) => handleChange("paymentProvider", e.target.value)}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    >
                                        {PAYMENT_PROVIDER_OPTIONS.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Start Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900">Event start date *</label>
                                <input
                                    type="datetime-local"
                                    required
                                    value={form.startDate}
                                    onChange={(e) => handleChange("startDate", e.target.value)}
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                />
                            </div>

                            {/* End Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900">Event end date *</label>
                                <input
                                    type="datetime-local"
                                    required
                                    value={form.endDate}
                                    onChange={(e) => handleChange("endDate", e.target.value)}
                                    className={`mt-2 w-full rounded-md border px-3 py-2 text-sm focus:outline-none ${dateError ? "border-red-400 focus:border-red-500" : "border-gray-300 focus:border-indigo-600"}`}
                                />
                                {dateError && (
                                    <p className="mt-1 text-xs text-red-600">{dateError}</p>
                                )}
                            </div>

                            {/* Category */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900">Category *</label>
                                <p className="mt-1 text-xs text-gray-500">Select a category that best describes your
                                    event.</p>
                                <select
                                    required
                                    value={form.category}
                                    onChange={(e) => handleCategoryChange(e.target.value)}
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                >
                                    {CATEGORY_OPTIONS.map((cat) => (
                                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Subcategory */}
                            {(SUBCATEGORY_MAP[form.category]?.length ?? 0) > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Subcategory</label>
                                    <p className="mt-1 text-xs text-gray-500">Optionally narrow down the type of
                                        event.</p>
                                    <select
                                        value={form.subCategory}
                                        onChange={(e) => handleChange("subCategory", e.target.value)}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    >
                                        <option value="">Select a subcategory…</option>
                                        {SUBCATEGORY_MAP[form.category].map((sub) => (
                                            <option key={sub} value={sub}>{sub}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Tags */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900">Tags</label>
                                <p className="mt-1 text-xs text-gray-500">Add keywords to help people discover your
                                    event. Press Enter or comma to add.</p>
                                {form.tags.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {form.tags.map((tag) => (
                                            <span key={tag}
                                                  className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-200">
                                                {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => removeTag(tag)}
                                                    className="ml-0.5 text-orange-400 hover:text-orange-700"
                                                >×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <input
                                    type="text"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === ",") {
                                            e.preventDefault();
                                            addTag(tagInput);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (tagInput.trim()) addTag(tagInput);
                                    }}
                                    placeholder="e.g. outdoor, networking, free…"
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                />
                            </div>

                            {/* Recurring toggle */}
                            <div className="md:col-span-2">
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={form.recurring}
                                        onClick={() => setForm((prev) => ({
                                            ...prev,
                                            recurring: !prev.recurring,
                                            recurrencePattern: "",
                                            recurrenceInterval: "",
                                            recurrenceEndDate: "",
                                        }))}
                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${form.recurring ? "bg-orange-500" : "bg-gray-200"}`}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition-transform ${form.recurring ? "translate-x-5" : "translate-x-0"}`}/>
                                    </button>
                                    <span
                                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
                                        <RefreshCw className="size-4 text-gray-500"/>
                                        Recurring event
                                    </span>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">Enable if this event repeats on a regular
                                    schedule.</p>
                            </div>

                            {/* Recurrence fields */}
                            {form.recurring && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-900">Recurrence pattern
                                            *</label>
                                        <p className="mt-1 text-xs text-gray-500">How often does this event repeat?</p>
                                        <select
                                            required={form.recurring}
                                            value={form.recurrencePattern}
                                            onChange={(e) => handleChange("recurrencePattern", e.target.value)}
                                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                        >
                                            <option value="">Select a pattern…</option>
                                            <option value="DAILY">Daily</option>
                                            <option value="WEEKLY">Weekly</option>
                                            <option value="BIWEEKLY">Biweekly</option>
                                            <option value="MONTHLY">Monthly</option>
                                            <option value="YEARLY">Yearly</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-900">Interval</label>
                                        <p className="mt-1 text-xs text-gray-500">Repeat every N occurrences of the
                                            pattern (e.g. every 2 weeks).</p>
                                        <input
                                            type="number"
                                            min={1}
                                            step={1}
                                            placeholder="1"
                                            value={form.recurrenceInterval}
                                            onChange={(e) => setForm((prev) => ({
                                                ...prev,
                                                recurrenceInterval: e.target.value === "" ? "" : parseInt(e.target.value, 10)
                                            }))}
                                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-900">Recurrence end
                                            date</label>
                                        <p className="mt-1 text-xs text-gray-500">When should the recurring series
                                            stop?</p>
                                        <input
                                            type="datetime-local"
                                            value={form.recurrenceEndDate}
                                            onChange={(e) => handleChange("recurrenceEndDate", e.target.value)}
                                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                        />
                                    </div>
                                </>
                            )}

                            {/* Description */}
                            <div className="md:col-span-2">
                                <MarkdownField
                                    title="Event description"
                                    description="Describe your event in detail. This will be shown on your public event page, so make it engaging and informative!"
                                    value={form.description ?? ""}
                                    onChange={(v) => handleChange("description", v)}/>
                            </div>
                        </div>
                    </div>

                    {/* Location Type */}
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="mb-3">
                            <p className="text-sm font-medium text-gray-900">Location type *</p>
                            <p className="mt-0.5 text-xs text-gray-500">Where will your event take place?</p>
                        </div>
                        <CardSelector
                            options={LOCATION_TYPE_OPTIONS}
                            value={form.locationType}
                            onChange={(v) => handleChange("locationType", v)}
                        />
                    </div>

                    {/* Physical location fields */}
                    {isPhysical && (
                        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
                            {form.locationType === "HYBRID" && (
                                <p className="mb-4 text-sm font-medium text-gray-700">Physical venue</p>
                            )}
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Venue name *</label>
                                    <input
                                        type="text"
                                        required={isPhysical}
                                        value={form.location}
                                        onChange={(e) => handleChange("location", e.target.value)}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Venue address *</label>
                                    <input
                                        type="text"
                                        required={isPhysical}
                                        value={form.address}
                                        onChange={(e) => handleChange("address", e.target.value)}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                </div>

                                {/* Map preview */}
                                <div className="md:col-span-2">
                                    <div className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                                        <div
                                            className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-3 py-2">
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-900">Map preview</p>
                                                <p className="text-xs text-gray-600">Based on the venue + address you
                                                    entered.</p>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                                                    <input
                                                        type="checkbox"
                                                        checked={appendCountry}
                                                        onChange={(e) => setAppendCountry(e.target.checked)}
                                                        className="size-4 rounded border-gray-300"
                                                    />
                                                    Append "Namibia"
                                                </label>

                                                <span
                                                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${confidenceBadge(level)}`}
                                                    title={reason}
                                                >
                                                    {level === "high" ? "Accurate" : level === "medium" ? "Maybe" : "Needs details"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="h-80">
                                            {mapQuery ? (
                                                <iframe
                                                    key={mapQuery}
                                                    title="Venue location preview"
                                                    width="100%"
                                                    height="100%"
                                                    loading="lazy"
                                                    className="h-full w-full border-0"
                                                    referrerPolicy="no-referrer-when-downgrade"
                                                    src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
                                                />
                                            ) : (
                                                <div
                                                    className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500">
                                                    Enter a venue and address to preview the location on the map.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Virtual location fields */}
                    {isVirtual && (
                        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
                            {form.locationType === "HYBRID" && (
                                <p className="mb-4 text-sm font-medium text-gray-700">Online details</p>
                            )}
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Platform *</label>
                                    <p className="mt-1 text-xs text-gray-500">Which platform will you host the event
                                        on?</p>
                                    <select
                                        required={isVirtual}
                                        value={form.virtualPlatform}
                                        onChange={(e) => handleChange("virtualPlatform", e.target.value)}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    >
                                        <option value="">Select a platform…</option>
                                        {VIRTUAL_PLATFORM_OPTIONS.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Event link *</label>
                                    <p className="mt-1 text-xs text-gray-500">The URL attendees will use to join.</p>
                                    <input
                                        type="url"
                                        required={isVirtual}
                                        placeholder="https://"
                                        value={form.virtualLink}
                                        onChange={(e) => handleChange("virtualLink", e.target.value)}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-3">
                        <Button type="button" variant="secondary" size="md"
                                onClick={() => navigate(isTournament ? "/business/tournaments" : "/business/events")}>
                            Exit
                        </Button>

                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            disabled={!canContinue || saving || loading}
                            onClick={goNext}
                        >
                            {isTournament ? "Continue to Tournament" : "Continue to Event Days"}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
}
